import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import Spinner from '../Spinner';
import styles from './Button.module.scss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const cln = classnames.bind(styles);
function Button({
  children,
  selected = false,
  size = 'big',
  disabled = false,
  variant = 'primary',
  className,
  onClick = () => {},
  iconOnly = false,
  loading = false,
  labelOnLoad = false,
  ...rest
}) {
  const classes = cln('Button', className, {
    'Button--selected': selected && !disabled,
    'Button--disabled': disabled,
    'Button--loading': loading,
    [`Button--${size}`]: size,
    'Button--iconOnly': iconOnly,
    [`Button--${variant}`]: true,
    'Button--label-loading': labelOnLoad
  });
  const spinnerClasses = cln('Spinner', {
    'Spinner--left': labelOnLoad
  });
  const labelStyles = {};
  if (loading && (typeof labelOnLoad === 'string' || labelOnLoad === false)) {
    labelStyles.visibility = 'hidden';
    labelStyles.display = 'block';
  }
  let Container = 'button';
  if (rest.type === 'label') {
    Container = 'label';
    delete rest.type; // eslint-disable-line
  }

  // rule is disable due to https://github.com/yannickcr/eslint-plugin-react/issues/1555
  // type prop is now required so that everyone set desired button type and be aware of the behavior
  /* eslint-disable react/button-has-type */
  return /*#__PURE__*/_jsxs(Container, {
    className: classes,
    disabled: disabled,
    onClick: !disabled && !loading && onClick ? onClick : undefined,
    ...rest,
    children: [loading && /*#__PURE__*/_jsx(Spinner, {
      speed: 80,
      variant: "folding",
      theme: variant === 'standard' ? 'dark' : 'light',
      strokeSize: "0.5",
      className: spinnerClasses
    }), loading && typeof labelOnLoad === 'string' && labelOnLoad, /*#__PURE__*/_jsx("span", {
      "data-testid": "button-label",
      className: cln('Label'),
      style: labelStyles,
      children: children
    })]
  });
}
Button.propTypes = {
  /**
   * This button can be used in two variants: primary (call to action) or standard (without a background).
   * */
  variant: PropTypes.oneOf(['primary', 'standard', 'danger', 'warning', 'notice']),
  /**
   * On of predefined sizes */
  size: PropTypes.oneOf(['big', 'small', 'compact']),
  /** Optional className to customize the button */
  className: PropTypes.string,
  /** If true then spinner is displayed */
  loading: PropTypes.bool,
  /** If true, displayed as square, useful for buttons with an icon only */
  iconOnly: PropTypes.bool,
  /** If set to true, a button is displayed in an active style and onClick function (if provided) will not be triggered */
  selected: PropTypes.bool,
  /** If set to true, a button is displayed in an inactive style and onClick function (if provided) will not be triggered */
  disabled: PropTypes.bool,
  /** If a button is in a loading state, it allows to display only a spinner or a spinner with a label.
   *  If set to false or empty string - displays a spinner only.
   *  If set to true - displays a spinner with an original button label.
   *  If set to custom string - displays a spinner with a custom string.
   */
  labelOnLoad: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Function to trigger after a button is clicked. NOTE! This function will not be triggered if a button is disabled or in loading state */
  onClick: PropTypes.func,
  /** @ignore */
  children: PropTypes.node.isRequired,
  /** It defines a type of a button. Please note that when this button is of the label type it works as a regular label tag and
   * therefor only "htmlFor", "form" and all props defined on this component are taken into account.
   */
  type: PropTypes.oneOf(['submit', 'button', 'reset', 'label']).isRequired
};
export default Button;